import { Injectable, inject } from '@angular/core';
import { CoreActions } from '@next/core-lib';
import { LocaleService } from '@next/core-lib/i18n';
import { NotificationActions } from '@next/core-lib/notification';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, finalize, map, mergeMap, startWith } from 'rxjs/operators';

import { CowListItem, ProductionState, ProductionSummary } from '../../models/production.model';
import { ProductionReportService } from '../../services/production-report.service';
import * as ProductionActions from '../actions/production.actions';

@Injectable()
export class ProductionEffects {
  private readonly actions$ = inject(Actions);
  private readonly productionReportService = inject(ProductionReportService);
  private readonly localeService = inject(LocaleService);
  private readonly store = inject<Store<ProductionState>>(Store);

  protected loadCowList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductionActions.loadCowListRequest),
      mergeMap(({ date }) =>
        this.productionReportService.getCows(date).pipe(
          map((cowList: CowListItem[]) => ProductionActions.loadCowListSuccess({ cowList })),
          catchError((errorResponse) => {
            const message = errorResponse.error?.message
              ? this.localeService.translate(errorResponse.error.message)
              : errorResponse.message;
            return of(NotificationActions.errorSnackbar({ message }));
          }),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  protected loadProductionSummary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductionActions.loadProductionSummaryRequest),
      mergeMap(({ date }) =>
        this.productionReportService.getSummary(date).pipe(
          map((productionSummary: ProductionSummary) =>
            ProductionActions.loadProductionSummarySuccess({ productionSummary }),
          ),
          catchError((errorResponse) => {
            const message = errorResponse.error?.message
              ? this.localeService.translate(errorResponse.error.message)
              : errorResponse.message;
            return of(NotificationActions.errorSnackbar({ message }));
          }),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );
}
